import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Loader2, Link2, AlertTriangle } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STIJL = {
  live: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "live, velden gevraagd": "bg-amber-100 text-amber-900 border-amber-200",
  "uitbreiding gevraagd": "bg-amber-100 text-amber-900 border-amber-200",
  "nog te bouwen door CRM": "bg-rose-100 text-rose-800 border-rose-200",
};

const CONTROLE_LABEL = {
  ok: "live geleverd",
  deels: "deels geleverd",
  ontbreekt: "nog niet geleverd",
  onbekend: "niet te controleren",
  fout: "controle mislukt",
};

const CONTROLE_STIJL = {
  ok: "bg-emerald-600 text-white border-emerald-600",
  deels: "bg-amber-500 text-white border-amber-500",
  ontbreekt: "bg-rose-600 text-white border-rose-600",
  onbekend: "bg-secondary text-muted-foreground",
  fout: "bg-rose-600 text-white border-rose-600",
};

const VeldTabel = ({ titel, velden }) => {
  const rijen = Object.entries(velden || {});
  if (rijen.length === 0) return null;
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titel}</p>
      <div className="mt-2 overflow-hidden rounded-md border">
        <table className="w-full text-left text-sm">
          <tbody>
            {rijen.map(([naam, info]) => (
              <tr key={naam} className="border-b last:border-0 align-top">
                <td className="w-1/3 px-3 py-2 font-mono text-xs">{naam}</td>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs text-muted-foreground">{info.type}</span>
                  {info.verplicht ? (
                    <span className="ml-2 text-xs font-medium text-foreground">verplicht</span>
                  ) : (
                    <span className="ml-2 text-xs text-muted-foreground">optioneel</span>
                  )}
                  {info.status && <span className="ml-2 text-xs font-semibold text-amber-700">{info.status}</span>}
                  {info.waarden && (
                    <div className="mt-1 text-xs text-muted-foreground">waarden: {info.waarden.join(" · ")}</div>
                  )}
                  {info.velden && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      subvelden: {Object.keys(info.velden).join(", ")}
                    </div>
                  )}
                  {info.voorbeeld && <div className="mt-1 text-xs text-muted-foreground">voorbeeld: {info.voorbeeld}</div>}
                  {info.toelichting && <div className="mt-1 text-xs">{info.toelichting}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Aansluiting = ({ item, controle }) => (
  <Card className="p-5" data-testid={`aansluiting-${item.id}`}>
    <div className="flex flex-wrap items-center gap-3">
      <span className="rounded bg-foreground px-2 py-1 font-mono text-xs font-semibold text-background">{item.methode}</span>
      <code className="font-mono text-sm">{item.pad}</code>
      <Badge variant="outline" className={STATUS_STIJL[item.status] || ""}>{item.status}</Badge>
      {controle && (
        <Badge variant="outline" className={CONTROLE_STIJL[controle.status] || ""} data-testid={`controle-${item.id}`}>
          {CONTROLE_LABEL[controle.status] || controle.status}
        </Badge>
      )}
    </div>
    <p className="mt-3 text-sm">{item.doel}</p>
    <p className="mt-1 text-xs text-muted-foreground">Autorisatie: {item.auth}</p>
    {controle?.ontbrekend?.length > 0 && (
      <p className="mt-2 text-xs" data-testid={`controle-ontbrekend-${item.id}`}>
        Nog niet geleverd: {controle.ontbrekend.map((n) => <code key={n} className="mr-1 font-mono">{n}</code>)}
      </p>
    )}
    <VeldTabel titel="Webshop verstuurt" velden={item.verstuurt} />
    <VeldTabel titel="Webshop verwacht terug" velden={item.verwacht_terug} />
    {item.foutcodes && (
      <div className="mt-4 text-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Foutcodes</p>
        <ul className="mt-1 space-y-1">
          {Object.entries(item.foutcodes).map(([code, uitleg]) => (
            <li key={code}><code className="font-mono text-xs">{code}</code> — {uitleg}</li>
          ))}
        </ul>
      </div>
    )}
    {item.afhandeling_crm && (
      <p className="mt-4 rounded-md bg-secondary p-3 text-sm">{item.afhandeling_crm}</p>
    )}
  </Card>
);

const IntegratieSpec = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [fout, setFout] = useState("");

  useEffect(() => {
    fetch(`${BASE}/integratie/${token}`)
      .then(async (res) => {
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Niet gevonden");
        return res.json();
      })
      .then(setData)
      .catch((e) => setFout(e.message));
  }, [token]);

  const groepen = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    data.aansluitingen.forEach((a) => {
      if (!map.has(a.groep)) map.set(a.groep, []);
      map.get(a.groep).push(a);
    });
    return [...map.entries()];
  }, [data]);

  if (fout) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center" data-testid="integratie-fout">
        <AlertTriangle className="mx-auto h-10 w-10 text-destructive" />
        <h1 className="mt-4 font-display text-2xl font-semibold">Link niet geldig</h1>
        <p className="mt-2 text-sm text-muted-foreground">{fout}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />Laden…
      </div>
    );
  }

  const aansluitingenUrl = `${BASE}/integratie/${token}/aansluitingen`;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="integratie-spec-page">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Read-only · versie {data.versie}</p>
      <h1 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">{data.titel}</h1>
      <p className="mt-3 text-base text-muted-foreground">
        Alles wat de webshop bij het CRM opvraagt of naar het CRM stuurt. CRM-basis:{" "}
        <code className="font-mono text-sm">{data.crm_basis_url}</code>
      </p>

      {data.controle && (
        <p className="mt-4 rounded-md border bg-secondary px-3 py-2 text-sm" data-testid="integratie-controle-tijd">
          Live status gecontroleerd op {new Date(data.controle.uitgevoerd_op).toLocaleString("nl-NL")} — per aansluiting
          hieronder zie je wat het CRM al levert.
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild data-testid="integratie-json-download">
          <a href={aansluitingenUrl} target="_blank" rel="noreferrer">
            <Download className="mr-2 h-4 w-4" />Aansluitingen als JSON
          </a>
        </Button>
        <Button
          variant="outline"
          onClick={() => navigator.clipboard?.writeText(aansluitingenUrl)}
          data-testid="integratie-copy-json-url"
        >
          <Link2 className="mr-2 h-4 w-4" />JSON-URL kopiëren
        </Button>
      </div>

      <Card className="mt-8 p-5" data-testid="integratie-afspraken">
        <h2 className="font-display text-lg font-semibold">Afspraken</h2>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm">
          {data.afspraken.map((a) => <li key={a}>{a}</li>)}
        </ul>
      </Card>

      {(data.wijzigingen || []).length > 0 && (
        <Card className="mt-6 border-amber-300 bg-amber-50 p-5" data-testid="integratie-wijzigingen">
          <h2 className="font-display text-lg font-semibold">Nieuw in versie {data.wijzigingen[0].versie}</h2>
          <p className="mt-1 text-xs text-amber-900">Deze aanpassingen zijn gevraagd en nog niet live in het CRM. Velden met het label GEVRAAGD hieronder horen hierbij.</p>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm">
            {data.wijzigingen[0].items.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </Card>
      )}

      {groepen.map(([groep, items]) => (
        <section key={groep} className="mt-10 space-y-4">
          <h2 className="font-display text-lg font-semibold">{groep}</h2>
          {items.map((item) => (
            <Aansluiting key={item.id} item={item} controle={data.controle?.per_aansluiting?.[item.id]} />
          ))}
        </section>
      ))}

      <Card className="mt-10 p-5" data-testid="integratie-checklist">
        <h2 className="font-display text-lg font-semibold">Testchecklist na de CRM-deploy</h2>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm">
          {data.testchecklist.map((c) => <li key={c}>{c}</li>)}
        </ol>
      </Card>
    </div>
  );
};

export default IntegratieSpec;
